/**
 * ThemeSwitcher — przycisk/panel wyboru motywu
 * Wyświetlany jako elegancki dropdown w nagłówku
 */

import { useState } from 'react';
import { Palette, Check } from 'lucide-react';
import { useThemeContext, type ThemePreset } from '@/hooks/useTheme';

function ThemeDot({ color, active }: { color: string; active: boolean }) {
  return (
    <span
      className="inline-block rounded-full transition-all duration-200"
      style={{
        width: 12,
        height: 12,
        background: color,
        boxShadow: active ? `0 0 8px ${color}` : 'none',
        border: active ? `2px solid ${color}` : '2px solid transparent',
        outline: active ? '1px solid rgba(255,255,255,0.3)' : 'none',
        flexShrink: 0,
      }}
    />
  );
}

export function ThemeSwitcher() {
  const { current, setTheme, themes } = useThemeContext();
  const [open, setOpen] = useState(false);
  const isSoft = current.id === 'soft';

  return (
    <div className="relative">
      {/* Przycisk otwierający */}
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground transition-all duration-200"
        style={{
          background: open
            ? 'rgba(var(--theme-accent-rgb),0.12)'
            : isSoft ? 'rgba(255,252,248,0.7)' : 'transparent',
          border: '1px solid',
          borderColor: open ? 'rgba(var(--theme-accent-rgb),0.30)' : 'rgba(var(--theme-accent-rgb),0.15)',
          boxShadow: isSoft ? '0 2px 8px rgba(160,100,70,0.08)' : 'none',
          borderRadius: isSoft ? '2rem' : undefined,
        }}
        title="Zmień motyw"
        aria-label="Zmień motyw kolorystyczny"
      >
        <Palette className="w-4 h-4" />
        <ThemeDot color={current.accentColor} active={false} />
      </button>

      {/* Dropdown */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />

          {/* Panel */}
          <div
            className="absolute right-0 top-full mt-2 z-50 rounded-xl overflow-hidden shadow-2xl"
            style={{
              minWidth: 230,
              background: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: isSoft ? '1.25rem' : undefined,
              boxShadow: isSoft
                ? '0 8px 32px rgba(160,100,70,0.14), 0 2px 8px rgba(160,100,70,0.08)'
                : '0 16px 48px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)',
            }}
          >
            {/* Header panelu */}
            <div
              className="px-4 py-3"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <p className="text-xs font-medium uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
                Motyw kolorystyczny
              </p>
            </div>

            {/* Lista motywów */}
            <div className="p-2">
              {themes.map((theme: ThemePreset) => {
                const isActive = theme.id === current.id;
                return (
                  <button
                    key={theme.id}
                    onClick={() => { setTheme(theme.id); setOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150 text-left group"
                    style={{
                      background: isActive ? `rgba(var(--theme-accent-rgb),0.10)` : 'transparent',
                      borderRadius: isSoft ? '0.875rem' : undefined,
                    }}
                    onMouseEnter={e => {
                      if (!isActive) (e.currentTarget as HTMLButtonElement).style.background =
                        isSoft ? 'rgba(196,106,80,0.07)' : 'rgba(255,255,255,0.04)';
                    }}
                    onMouseLeave={e => {
                      if (!isActive) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <ThemeDot color={theme.accentColor} active={isActive} />
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-sm font-medium leading-tight"
                        style={{ color: isActive ? 'var(--foreground)' : 'var(--muted-foreground)' }}
                      >
                        {theme.label}
                      </p>
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--muted-foreground)', opacity: 0.65 }}>
                        {theme.description}
                      </p>
                    </div>
                    {isActive && (
                      <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: theme.accentColor }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div
              className="px-4 py-2"
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <p className="text-xs" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }}>
                Wybór zapamiętywany lokalnie
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
